"use client";
import React, { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { XOutlined } from "@ant-design/icons";
import ReCAPTCHA from "react-google-recaptcha";
import api from "@/config/api";

export default function ContactUsPage() {
  const pathname = usePathname();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const recaptchaRef = useRef(null);

  // Get reCAPTCHA site key from environment
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  // Check if captcha is enabled (only if site key is provided)
  const isCaptchaEnabled = !!recaptchaSiteKey;

  // Generate breadcrumb from pathname
  const generateBreadcrumb = (path) => {
    if (!path) return [];

    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Home', path: '/' }];

    let currentPath = '';
    segments.forEach((segment) => {
      currentPath += `/${segment}`;
      // Capitalize first letter and replace hyphens with spaces
      const name = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({ name, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumb(pathname);

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (name.trim().length > 50) {
      return "Name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      return "Name should only contain letters and spaces";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile.trim()) {
      return "Mobile number is required";
    }
    // Allow digits, spaces, hyphens, and plus sign for international formats
    const mobileRegex = /^[\d\s\-\+()]+$/;
    if (!mobileRegex.test(mobile.trim())) {
      return "Please enter a valid mobile number";
    }
    // Remove non-digits for length check
    const digitsOnly = mobile.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return "Mobile number must be at least 10 digits";
    }
    if (digitsOnly.length > 15) {
      return "Mobile number must be less than 15 digits";
    }
    return "";
  };

  const validateSubject = (subject) => {
    if (!subject.trim()) {
      return "Subject is required";
    }
    if (subject.trim().length < 3) {
      return "Subject must be at least 3 characters";
    }
    if (subject.trim().length > 100) {
      return "Subject must be less than 100 characters";
    }
    return "";
  };

  const validateMessage = (message) => {
    if (!message.trim()) {
      return "Message is required";
    }
    if (message.trim().length < 10) {
      return "Message must be at least 10 characters";
    }
    if (message.trim().length > 1000) {
      return "Message must be less than 1000 characters";
    }
    return "";
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return validateName(value);
      case "email":
        return validateEmail(value);
      case "mobile":
        return validateMobile(value);
      case "subject":
        return validateSubject(value);
      case "message":
        return validateMessage(value);
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      const error = validateField(name, value);
      if (!error) {
        const newErrors = { ...errors };
        delete newErrors[name];
        setErrors(newErrors);
      } else {
        setErrors({
          ...errors,
          [name]: error,
        });
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });

    const error = validateField(name, value);
    if (error) {
      setErrors({
        ...errors,
        [name]: error,
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  // Separate function to handle the actual form submission
  const submitContactForm = async (token = null) => {
    setLoading(true);
    setSubmitStatus(null);

    try {
      console.log("🔄 Submitting contact form:", formData);

      const requestData = {
        'contact_name': formData.name.trim(),
        'contact_email': formData.email.trim(),
        'contact_mobile': formData.mobile.trim(),
        'contact_subject': formData.subject.trim(),
        'contact_message': formData.message.trim(),
      };

      // Only include captcha token if captcha is enabled
      if (isCaptchaEnabled && token) {
        requestData.captcha_token = token;
      }

      const response = await api.post('/contact-us', requestData);


      console.log("✅ Contact form submitted successfully:", response.data);
      setSubmitStatus('success');

      // Reset form, errors, and captcha
      setFormData({
        name: "",
        email: "",
        mobile: "",
        subject: "",
        message: "",
      });
      setErrors({});
      setTouched({});
      setCaptchaToken(null);
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

    } catch (error) {
      console.error("❌ Error submitting contact form:", error);
      setSubmitStatus('error');

      // Reset captcha on error (but keep it visible for retry)
      setCaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // Show API error if available
      if (error.response?.data?.message) {
        setErrors({
          ...errors,
          _api: error.response.data.message,
        });
      } else {
        setErrors({
          ...errors,
          _api: "Failed to send message. Please try again later.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate form and get validation errors
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    // Set errors and check if form is valid
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 0);
      }
      return;
    }

    // If captcha is enabled and not shown yet, show it
    if (isCaptchaEnabled && !showCaptcha) {
      setShowCaptcha(true);
      // Scroll to captcha
      setTimeout(() => {
        const captchaElement = document.querySelector('[data-captcha]');
        if (captchaElement) {
          captchaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    // Validate captcha if it's enabled and shown
    if (isCaptchaEnabled && showCaptcha && !captchaToken) {
      setErrors({
        ...newErrors,
        captcha: "Please complete the captcha verification",
      });
      // Scroll to captcha
      setTimeout(() => {
        const captchaElement = document.querySelector('[data-captcha]');
        if (captchaElement) {
          captchaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
      return;
    }

    // Clear any existing errors
    setErrors({});

    // If captcha is not enabled, or if it's enabled and completed, submit directly
    if (!isCaptchaEnabled || (isCaptchaEnabled && captchaToken)) {
      submitContactForm(captchaToken);
    }
  };

  // Handle captcha change
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    // Clear captcha error if token exists
    if (token && errors.captcha) {
      const newErrors = { ...errors };
      delete newErrors.captcha;
      setErrors(newErrors);
    }

    // If captcha is completed and enabled, automatically submit the form
    if (token && isCaptchaEnabled) {
      submitContactForm(token);
    }
  };

  // Handle captcha expired
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    // Keep captcha visible for retry
    setShowCaptcha(true);
  };

  // Auto-trigger the reCAPTCHA checkbox when it appears to show image challenge
  useEffect(() => {
    if (showCaptcha && isCaptchaEnabled && recaptchaRef.current) {
      // Wait for reCAPTCHA to fully render
      const timer = setTimeout(() => {
        try {
          // Method 1: Try to get widget ID and execute
          if (window.grecaptcha && recaptchaRef.current) {
            try {
              const widgetId = recaptchaRef.current._widgetId;
              if (widgetId !== undefined) {
                // Try to get response first - if empty, try to execute
                const response = window.grecaptcha.getResponse(widgetId);
                if (!response) {
                  // Try to trigger the challenge
                  window.grecaptcha.execute(widgetId);
                }
              }
            } catch (e) {
              console.log('Method 1 failed, trying alternative');
            }
          }

          // Method 2: Try to find and click the iframe
          setTimeout(() => {
            try {
              const iframe = document.querySelector('.g-recaptcha iframe');
              if (iframe) {
                // Try clicking on the iframe's bounding box
                const rect = iframe.getBoundingClientRect();
                const clickX = rect.left + rect.width / 2;
                const clickY = rect.top + rect.height / 2;

                // Create a synthetic click event at the iframe position
                const clickEvent = new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                  clientX: clickX,
                  clientY: clickY,
                  button: 0
                });

                // Dispatch on the iframe element
                iframe.dispatchEvent(clickEvent);

                // Also try clicking on the parent container
                const parent = iframe.parentElement;
                if (parent) {
                  parent.dispatchEvent(clickEvent);
                }
              }
            } catch (error) {
              console.log('Method 2 failed:', error);
            }
          }, 300);

        } catch (error) {
          console.log('Auto-trigger failed. User will need to click the checkbox manually.');
        }
      }, 800); // Increased delay to ensure reCAPTCHA is fully loaded

      return () => clearTimeout(timer);
    }
  }, [showCaptcha, isCaptchaEnabled]);

  return (
    <div className="relative min-h-screen bg-white">
      {/* Hero Banner Section */}
      <div
        className="relative w-full overflow-hidden flex flex-col items-start justify-center py-14 md:py-18 lg:py-20 xl:py-21 2xl:py-22 px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-[140px] box-border bg-cover bg-no-repeat bg-top text-left"
        style={{ backgroundImage: "url('/images/contact-us_banner.webp')" }}
      >
        {/* Overlay - Only for banner image */}
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative z-10 flex flex-col items-start gap-3 md:gap-[19px]">
          <div className="relative text-3xl md:text-5xl lg:text-[48px] xl:text-[52px] 2xl:text-[56px] leading-tight lg:leading-[48px] xl:leading-[52px] 2xl:leading-[56px] font-medium text-white">
            Contact Us
          </div>
          <div className="relative text-sm md:text-base leading-6 font-semibold text-[#f4f4f4]">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path}>
                {index > 0 && <span className="mx-2">|</span>}
                {index === breadcrumbs.length - 1 ? (
                  <span>{crumb.name}</span>
                ) : (
                  <Link
                    href={crumb.path}
                    className="hover:underline transition-opacity hover:opacity-80"
                  >
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative w-full max-w-[1631px] mx-auto px-6 md:px-12 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-5 lg:gap-4 xl:gap-5 2xl:gap-5 mt-0 pb-16">
        {/* Left Column - Contact Info */}
        <div className="w-full lg:w-[801px] flex flex-col items-start py-8 md:py-12 lg:py-14 xl:py-15 2xl:py-16 px-6 md:px-12 lg:px-14 xl:px-20 2xl:px-[148px] box-border gap-8 md:gap-12">
          <div className="w-full lg:w-[517px] flex flex-col items-start gap-4 md:gap-[19px]">
            <div className="self-stretch flex flex-col items-start text-left">
              <div className="relative text-2xl md:text-3xl lg:text-[30px] xl:text-[33px] 2xl:text-[36px] leading-tight lg:leading-[30px] xl:leading-[33px] 2xl:leading-9 font-medium text-[#3a599c]">
                Need Help? Contact Us
              </div>
            </div>
            <div className="self-stretch relative text-sm md:text-base leading-6 text-black">
              Have a question or need support? We’re just a message away.
              Please submit a contact request and share as many details as possible so our team can assist you quickly and effectively.            </div>
            <div className="self-stretch relative text-sm md:text-base leading-6 text-black">
              If you’re facing a technical issue, describe the problem clearly so we can identify and fix it faster
            </div>
            <div className="self-stretch relative text-sm md:text-base leading-6 text-black">
              For billing or payment-related queries, include your account details and a short explanation or email us directly at support@hyrelancer.com
            </div>
            <div className="self-stretch relative text-sm md:text-base leading-6 text-black">
              Your time matters to us, and we’re committed to resolving your concerns with care and efficiency                        </div>
          </div>

          <div className="w-full lg:w-[366px] flex flex-col items-start gap-6 md:gap-8 text-[#888]">
            {/* Phone */}


            {/* Email */}
            <div className="relative text-sm md:text-base leading-6 font-semibold">
              <p className="m-0">Need live support?</p>
              <p className="m-0 text-black">info@hyrelancer.com</p>
            </div>

            {/* Social Media */}
            <div className="self-stretch flex flex-col items-start text-center text-black">
              <div className="w-full lg:w-[366px] flex flex-col items-start justify-center gap-4">
                <div className="relative text-sm md:text-base leading-6 font-semibold">
                  Follow Us
                </div>
                <div className="flex items-center gap-2">
                  <SocialIcon
                    href="https://www.facebook.com/people/Hyrelancer/61577473019946/#"
                    icon={<Facebook className="h-5 w-5" />}
                  />
                  <SocialIcon
                    href="https://www.instagram.com/hyrelancer/"
                    icon={<Instagram className="h-5 w-5" />}
                  />
                  <SocialIcon
                    href="https://x.com"
                    icon={<XOutlined className="h-5 w-5" />}
                  />
                  <SocialIcon
                    href={"https://www.linkedin.com/company/hyrelancerindia/"}
                    icon={<Linkedin className="h-6 w-6" />} />
                  <SocialIcon icon={<Youtube className="h-6 w-6" />} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Form - Overlapping */}
        {/* On md, form appears below text content */}
        <div className="w-full lg:w-auto relative z-20 order-last lg:order-none lg:-mt-32 xl:-mt-32 2xl:-mt-32">
          <div className="shadow-[0px_4px_20px_rgba(58,89,156,0.18)] rounded-[32px] bg-white overflow-hidden flex items-start justify-center py-12 md:py-20 lg:py-16 xl:py-26 2xl:py-28 px-8 md:px-16 lg:px-12 xl:px-24 2xl:px-[140px]">
            <div className="w-full md:w-[382px] flex flex-col items-center justify-center gap-8 md:gap-10 lg:gap-11 xl:gap-11 2xl:gap-12">
              <div className="self-stretch flex flex-col items-start text-center">
                <div className="relative text-2xl md:text-3xl lg:text-[30px] xl:text-[33px] 2xl:text-[36px] leading-tight lg:leading-[30px] xl:leading-[33px] 2xl:leading-9 font-medium text-[#3a599c] w-full">
                  Send us a message
                </div>
              </div>

              <form onSubmit={handleSubmit} className="w-full md:w-[382px] flex flex-col items-start gap-4">
                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <div className="w-full p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      Message sent successfully! We'll get back to you soon.
                    </p>
                  </div>
                )}
                {submitStatus === 'error' && errors._api && (
                  <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm font-medium">
                      ❌ {errors._api}
                    </p>
                  </div>
                )}

                {/* Name Input */}
                <div className="w-full relative">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full h-[49px] rounded-[24px] bg-white border-solid border-[1px] box-border px-6 text-base leading-6 text-[#888] placeholder:text-[#888] focus:outline-none transition-colors ${errors.name && touched.name
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[#888] focus:border-[#3a599c]'
                      }`}
                  />
                  {errors.name && touched.name && (
                    <p className="mt-1 text-xs text-red-600 px-2">{errors.name}</p>
                  )}
                </div>

                {/* Email Input */}
                <div className="w-full relative">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full h-[49px] rounded-[24px] bg-white border-solid border-[1px] box-border px-6 text-base leading-6 text-[#888] placeholder:text-[#888] focus:outline-none transition-colors ${errors.email && touched.email
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[#888] focus:border-[#3a599c]'
                      }`}
                  />
                  {errors.email && touched.email && (
                    <p className="mt-1 text-xs text-red-600 px-2">{errors.email}</p>
                  )}
                </div>

                {/* Mobile Number Input */}
                <div className="w-full relative">
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="Mobile Number"
                    value={formData.mobile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full h-[49px] rounded-[24px] bg-white border-solid border-[1px] box-border px-6 text-base leading-6 text-[#888] placeholder:text-[#888] focus:outline-none transition-colors ${errors.mobile && touched.mobile
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[#888] focus:border-[#3a599c]'
                      }`}
                  />
                  {errors.mobile && touched.mobile && (
                    <p className="mt-1 text-xs text-red-600 px-2">{errors.mobile}</p>
                  )}
                </div>

                {/* Subject Input */}
                <div className="w-full relative">
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full h-[49px] rounded-[24px] bg-white border-solid border-[1px] box-border px-6 text-base leading-6 text-[#888] placeholder:text-[#888] focus:outline-none transition-colors ${errors.subject && touched.subject
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[#888] focus:border-[#3a599c]'
                      }`}
                  />
                  {errors.subject && touched.subject && (
                    <p className="mt-1 text-xs text-red-600 px-2">{errors.subject}</p>
                  )}
                </div>

                {/* Message Textarea */}
                <div className="self-stretch relative">
                  <textarea
                    name="message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={`w-full h-[116px] rounded-[24px] bg-white border-solid border-[1px] box-border py-[19px] px-6 text-base leading-6 text-[#888] placeholder:text-[#888] focus:outline-none resize-none transition-colors ${errors.message && touched.message
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[#888] focus:border-[#3a599c]'
                      }`}
                  />
                  {errors.message && touched.message && (
                    <p className="mt-1 text-xs text-red-600 px-2">{errors.message}</p>
                  )}
                  {formData.message && !errors.message && (
                    <p className="mt-1 text-xs text-gray-500 px-2 text-right">
                      {formData.message.trim().length}/1000 characters
                    </p>
                  )}
                </div>

                {/* reCAPTCHA - Only show after user clicks submit button */}
                {isCaptchaEnabled && showCaptcha && (
                  <div className="w-full flex flex-col items-start gap-2" data-captcha>
                    <div className="w-full px-0 py-3 flex items-start justify-start">
                      <div className="transform scale-110 origin-left">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaSiteKey}
                          onChange={handleCaptchaChange}
                          onExpired={handleCaptchaExpired}
                          theme="light"
                        />
                      </div>
                    </div>
                    {errors.captcha && (
                      <p className="text-xs text-red-600 px-2">{errors.captcha}</p>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div className="w-full flex flex-col items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading || (isCaptchaEnabled && showCaptcha && !captchaToken)}
                    className={`self-stretch h-12 rounded-[24px] flex items-center justify-center py-4 px-6 box-border text-white text-base leading-6 font-semibold transition-colors ${loading || (isCaptchaEnabled && showCaptcha && !captchaToken)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#3a599c] hover:bg-[#2f4a7f] cursor-pointer'
                      }`}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </div>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SocialIcon({ icon, href }) {
  const className = "h-12 w-12 relative flex items-center justify-center rounded-full border-2 border-gray-300 bg-transparent hover:border-[#3a599c] hover:bg-[#3a599c]/5 transition-all duration-200";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        <span className="text-gray-700">{icon}</span>
      </a>
    );
  }

  return (
    <button className={className}>
      <span className="text-gray-700">{icon}</span>
    </button>
  );
}

